import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Box, FormControl, Grid, MenuItem, TextareaAutosize } from '@material-ui/core';
import { btMediumGrayColor } from '../../../assets/jss/nextjs-material-kit-pro';
import LuxonUtils from '@date-io/luxon';
import { dateUtcToS } from '../../../util/dateUtil';
import ExpandingList from '../../ExpandingList/ExpandingList';
import { createPrintedList } from '../../../util/stringUtil';
import { calculateCosts } from '../../../util/costUtils';
import PlanSection from './PlanSection';
import EmploymentPlan from './EmploymentPlan';
import TransportationPlanV2 from './TransportationPlanV2';
import SpiritualInformationV2 from './SpiritualInformationV2';
import HealthInsurancePlanV2 from './HealthInsurancePlanV2';
import HousingPlanV2 from './HousingPlanV2';

const styles = makeStyles((theme) => ({
  box: {
    padding: '30px',
  },
  boxA: {
    '@media print': {
      backgroundColor: 'transparent',
    },
    '@media screen': {
      backgroundColor: '#F2F2FA',
    },
  },
  boxB: {
    '@media print': {
      backgroundColor: 'transparent',
    },
    '@media screen': {
      //backgroundColor: '#f6fafd',
      backgroundColor: '#eaf4fb',
    },
  },
  textSpacer: {
    margin: '15px 0 5px 0',
  },
  rowSpacer: {
    height: 30,
  },
  textRowSpacer: {
    [theme.breakpoints.up('sm')]: {
      height: 50,
    },
  },
  textFieldBackground: {
    backgroundColor: 'white',
  },
  textRowLabelSpacer: {
    [theme.breakpoints.down('md')]: {
      height: 30,
      paddingTop: 5,
    },
    [theme.breakpoints.up('md')]: {
      height: 50,
      paddingTop: 8,
    },
  },
  [theme.breakpoints.down('xs')]: {
    textField: {
      fontSize: '0.75rem',
    },
    root: {
      '& .MuiFormLabel-root': {
        fontSize: '0.75rem',
      },
    },
  },
  bold: {
    fontWeight: 600,
  },
  subtleBold: {
    fontWeight: 500,
    color: btMediumGrayColor,
  },
  subtleBoldLabel: {
    fontWeight: 500,
    fontSize: '0.95rem',
  },
  textAreaFont: {
    fontSize: '0.938rem',
    fontFamily: 'Source Sans Pro, Helvetica, Roboto, Arial, sans-serif',
    padding: 10,
  },
  textAreaFixed: {
    resize: 'none',
    width: '100%',
    height: '60px',
    minHeight: '60px',
    maxHeight: '60px',
  },
  printedContent: {
    '@media print': {
      marginBottom: 10,
    },
    '@media screen': {
      display: 'none',
    },
  },
  unprintedContent: {
    '@media print': {
      display: 'none',
    },
    '@media screen': {},
  },
  alignPrintedContent: {
    lineHeight: '25px',
    verticalAlign: 'top',
  },
  subTitle: {
    margin: '15px 0',
    fontSize: '1.05rem',
    fontWeight: 500,
  },
  firstSectionSpacer: {
    height: 40,
  },
  sectionSpacer: {
    height: 60,
    padding: '10px 0',
  },
  sectionTitle: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.00rem',
      fontWeight: 600,
      margin: '8px 0',
    },
    [theme.breakpoints.up('sm')]: {
      fontSize: '1.10rem',
      fontWeight: 600,
      margin: '15px 0',
    },
  },
  reminder: {
    color: 'blue',
    fontSize: '1.05rem',
    margin: '10px 0',
  },
}));

export const OPTIONS = [
  { value: 'college', label: '4-Year College' },
  { value: '2-year-college', label: '2-Year College' },
  { value: 'career_training', label: 'Career Training' },
  { value: 'military', label: 'Military' },
  { value: 'workforce', label: 'Workforce' },
];

const getPlanMenuItems = () => {
  return OPTIONS.map((option) => (
    <MenuItem key={option.value || 'none'} value={option.value}>
      {option.label}
    </MenuItem>
  ));
};

const MyFuturePlan = (props) => {
  const {
    form,
    setForm,
    saveMilitaryMEPSDateTask,
    saveMilitaryBasicTrainingDateTask,
    classes: formClasses,
    saveTalkedToManagerTask,
    disabled,
    saveDriversLicenseTask,
    saveDriversEdTask,
    saveLearnersPermitTask,
    saveAutoInsuranceTask,
    handleHaveVehicle,
    saveHealthInsuranceTask,
  } = props;
  const classes = { ...styles(), ...(formClasses || {}) };
  if (form) {
    let updated = false;
    const newForm = { ...form };
    if (!Array.isArray(newForm.scholarshipsA) || newForm.scholarshipsA.length < 3) {
      newForm.scholarshipsA = Array(3)
        .fill()
        .map(() => ({ name: '' }));
      updated = true;
    }
    if (!Array.isArray(newForm.scholarshipsB) || newForm.scholarshipsB.length < 3) {
      newForm.scholarshipsB = Array(3)
        .fill()
        .map(() => ({ name: '' }));
      updated = true;
    }
    if (!Array.isArray(newForm.loansA) || newForm.loansA.length < 3) {
      newForm.loansA = Array(3)
        .fill()
        .map(() => ({ name: '' }));
      updated = true;
    }
    if (!Array.isArray(newForm.loansB) || newForm.loansB.length < 3) {
      newForm.loansB = Array(3)
        .fill()
        .map(() => ({ name: '' }));
      updated = true;
    }
    if (typeof newForm.nameLocationA !== 'string') {
      newForm.nameLocationA = '';
      updated = true;
    }
    if (typeof newForm.nameLocationB !== 'string') {
      newForm.nameLocationB = '';
      updated = true;
    }
    if (updated) setForm(newForm);
  }

  // Derive a safe value for the Plan A select to avoid MUI out-of-range warnings
  const allowedPlanValues = ['', ...OPTIONS.map((o) => o.value)];
  const rawPlanValue = form?.myFuturePlanA;
  const safePlanValue = allowedPlanValues.includes(rawPlanValue) ? rawPlanValue : '';

  // Derive a safe value for the Plan B select to avoid MUI out-of-range warnings
  const rawPlanValueB = form?.myFuturePlanB;
  const safePlanValueB = allowedPlanValues.includes(rawPlanValueB) ? rawPlanValueB : '';

  return (
    <React.Fragment>
      <Grid container direction="row" className={classes.sectionSpacer}>
        <Grid item xs={12} align="left">
          <section align="left" className={classes.sectionTitle}>
            My Future Plan
          </section>
        </Grid>
      </Grid>
      <PlanSection
        planType="A"
        form={form}
        setForm={setForm}
        disabled={disabled}
        classes={classes}
        OPTIONS={OPTIONS}
        getPlanMenuItems={getPlanMenuItems}
        createPrintedList={createPrintedList}
        calculateCosts={calculateCosts}
        dateUtcToS={dateUtcToS}
        saveMilitaryMEPSDateTask={saveMilitaryMEPSDateTask}
        saveMilitaryBasicTrainingDateTask={saveMilitaryBasicTrainingDateTask}
        safePlanValue={safePlanValue}
        ExpandingList={ExpandingList}
        LuxonUtils={LuxonUtils}
      />
      <summary>
        <section className={classes.unprintedContent} style={{ textAlign: 'left', marginTop: '25px', marginBottom: '10px' }}>
          In the event that your Plan A unexpectedly changes, due to circumstances beyond your control, please list below what your Plan B (back up
          plan) would be.
        </section>
        <div className={classes.printedContent}>
          In the event that your Plan A unexpectedly changes, due to circumstances beyond your control, please list below what your Plan B (back up
          plan) would be.
        </div>
      </summary>
      <PlanSection
        planType="B"
        form={form}
        setForm={setForm}
        disabled={disabled}
        classes={classes}
        OPTIONS={OPTIONS}
        getPlanMenuItems={getPlanMenuItems}
        createPrintedList={createPrintedList}
        calculateCosts={calculateCosts}
        dateUtcToS={dateUtcToS}
        saveMilitaryMEPSDateTask={saveMilitaryMEPSDateTask}
        saveMilitaryBasicTrainingDateTask={saveMilitaryBasicTrainingDateTask}
        safePlanValue={safePlanValueB}
        ExpandingList={ExpandingList}
        LuxonUtils={LuxonUtils}
      />
      <Box sx={{ width: '95%' }}>
        <Grid container direction="row" className={classes.sectionSpacer} style={{ marginTop: '25px', marginBottom: '-20px', padding: '0' }}>
          <Grid item xs={12} align="left">
            <section align="left" className={classes.textRowLabelSpacer} style={{ marginBottom: '0', paddingBottom: '0' }}>
              Is there a period of time between your departure or graduation and the start of your Plan A or Plan B? If so, what are your plans for
              managing that gap?
            </section>
          </Grid>
        </Grid>

        <Grid container direction="row" className={classes.unprintedContent} style={{ marginTop: '-10px', padding: '0' }}>
          <Grid item xs={12} className={classes.textRowSpacer} style={{ marginTop: '0px', padding: '0' }}>
            <FormControl fullWidth>
              <TextareaAutosize
                aria-label="gap-period-plan"
                minRows={2}
                maxRows={5}
                maxLength={500}
                value={form.gapPeriodPlan}
                onChange={(event) => setForm({ ...form, gapPeriodPlan: event.target.value })}
                disabled={disabled}
                className={`${classes.textAreaFont} ${classes.textAreaFixed}`}
                style={{
                  resize: 'none',
                  width: '100%',
                  height: '60px',
                  minHeight: '60px',
                  maxHeight: '60px',
                  color: disabled && 'rgba(0, 0, 0, 0.4)',
                  marginTop: '0',
                  paddingTop: '0',
                }}
              />
            </FormControl>
          </Grid>
        </Grid>
        <Grid container direction="row" className={classes.printedContent}>
          <Grid item xs={12}>
            <label className={classes.alignPrintedContent}>Location</label>
          </Grid>
          <Grid item xs={12}>
            <summary className={classes.alignPrintedContent}>{form.gapPeriodPlan || null}</summary>
          </Grid>
        </Grid>
        <HousingPlanV2 form={form} setForm={setForm} disabled={disabled} housingPlanText={'Location'} label={'General Expectations'} />
        <Grid container direction="row" className={classes.sectionSpacer}>
          <Grid item xs={12} align="left">
            <section align="left" className={classes.sectionSubTitle}>
              Employment Plan
            </section>
          </Grid>
        </Grid>
        <EmploymentPlan
          form={form}
          setForm={setForm}
          disabled={disabled}
          saveTalkedToManagerTask={saveTalkedToManagerTask}
          hideManagerField={true}
          showStartDate={true}
        />
        <TransportationPlanV2
          form={form}
          setForm={setForm}
          disabled={disabled}
          saveDriversLicenseTask={saveDriversLicenseTask}
          saveDriversEdTask={saveDriversEdTask}
          saveLearnersPermitTask={saveLearnersPermitTask}
          saveAutoInsuranceTask={saveAutoInsuranceTask}
          handleHaveVehicle={handleHaveVehicle}
        />
        <SpiritualInformationV2 className={classes.topMargin10} form={form} setForm={setForm} disabled={disabled} />
        <HealthInsurancePlanV2 form={form} setForm={setForm} disabled={disabled} saveHealthInsuranceTask={saveHealthInsuranceTask} />
      </Box>
    </React.Fragment>
  );
};

export default MyFuturePlan;
